import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useActions } from "../context/ActionsContext";
import { PageHeader, TableWrap, Th, Td, Loading, EmptyState, Badge } from "../components/shared";
import { formatINR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, UserPlus, ChevronRight } from "lucide-react";

export function AgeBadge({ days, testid }) {
  if (days === null || days === undefined) return <span className="text-slate-300">-</span>;
  const cls = days > 90 ? "bg-rose-50 text-rose-700 border-rose-200" : days > 60 ? "bg-orange-50 text-orange-700 border-orange-200" : days > 30 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return <span data-testid={testid}><Badge cls={cls}>{days}d</Badge></span>;
}

function PayeeCard({ p, onClick }) {
  const s = p.summary; const a = p.aging || {};
  return (
    <button onClick={onClick} data-testid={`payee-card-${p.id}`} className="w-full text-left bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm active:scale-[0.99] transition-transform">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">{p.name}</div>
          <div className="text-[11px] text-slate-400 truncate">{p.type}{p.company_name ? ` · ${p.company_name}` : ""}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300 shrink-0 mt-1" />
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-xs">
        <div><div className="text-[10px] uppercase text-slate-400">Expense</div><div className="font-mono font-semibold text-amber-600">{formatINR(s.total_expense)}</div></div>
        <div><div className="text-[10px] uppercase text-slate-400">Paid</div><div className="font-mono text-rose-600">{formatINR(s.total_paid)}</div></div>
        <div><div className="text-[10px] uppercase text-slate-400">Payable</div><div className="font-mono font-semibold text-teal-600 flex items-center gap-1.5">{formatINR(s.payable_pending)} <AgeBadge days={a.oldest_payable_days} /></div></div>
        <div><div className="text-[10px] uppercase text-slate-400">Advance</div><div className="font-mono text-violet-600 flex items-center gap-1.5">{formatINR(s.advance_pending)} <AgeBadge days={a.oldest_advance_days} /></div></div>
      </div>
    </button>
  );
}

export default function Payees() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { open } = useActions();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payees?search=${encodeURIComponent(search)}`);
      setItems(data.items);
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("prabhat:refresh", h);
    return () => window.removeEventListener("prabhat:refresh", h);
  }, [load]);

  const sorted = [...items].sort((x, y) => {
    if (sort === "oldest_payable") return (y.aging?.oldest_payable_days ?? -1) - (x.aging?.oldest_payable_days ?? -1);
    if (sort === "oldest_advance") return (y.aging?.oldest_advance_days ?? -1) - (x.aging?.oldest_advance_days ?? -1);
    if (sort === "payable") return y.summary.payable_pending - x.summary.payable_pending;
    return 0;
  });

  return (
    <div>
      <PageHeader title="Payees & Agencies" subtitle={`${items.length} payees`}
        actions={<Button onClick={() => open("add_payee")} data-testid="add-payee-btn" className="bg-[#0F2942] hover:bg-[#163a5c] gap-1.5"><UserPlus className="h-4 w-4" /> Add Payee</Button>} />
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <Input data-testid="payee-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search payees" className="pl-9" />
        </div>
        <select data-testid="payee-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 text-sm rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3">
          <option value="recent">Sort: Recent</option>
          <option value="oldest_payable">Sort: Oldest unpaid bill</option>
          <option value="oldest_advance">Sort: Oldest unadjusted advance</option>
          <option value="payable">Sort: Highest payable</option>
        </select>
      </div>
      {loading ? <Loading /> : items.length === 0 ? <EmptyState title="No payees yet" sub="Add agencies, vendors or staff to track payments and advances." /> : (
        <>
          <div className="grid gap-3 md:hidden" data-testid="payee-cards">
            {sorted.map((p) => <PayeeCard key={p.id} p={p} onClick={() => navigate(`/payees/${p.id}`)} />)}
          </div>
          <div className="hidden md:block">
            <TableWrap>
              <thead><tr>
                <Th>Name</Th><Th>Type</Th>
                <Th className="text-right">Total Expense</Th><Th className="text-right">Total Paid</Th>
                <Th className="text-right">Advance Pending</Th><Th className="text-right">Payable Pending</Th>
                <Th>Oldest Bill</Th><Th>Oldest Adv</Th><Th>Last Txn</Th>
              </tr></thead>
              <tbody>
                {sorted.map((p) => (
                  <tr key={p.id} data-testid={`payee-row-${p.id}`} onClick={() => navigate(`/payees/${p.id}`)} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer">
                    <Td className="font-medium text-slate-900 dark:text-slate-100">{p.name}{p.company_name ? <div className="text-[10px] text-slate-400">{p.company_name}</div> : null}</Td>
                    <Td><Badge cls="bg-slate-100 text-slate-700 border-slate-200">{p.type}</Badge></Td>
                    <Td className="text-right font-mono text-amber-600 font-semibold">{formatINR(p.summary.total_expense)}</Td>
                    <Td className="text-right font-mono text-rose-600">{formatINR(p.summary.total_paid)}</Td>
                    <Td className="text-right font-mono text-violet-600">{formatINR(p.summary.advance_pending)}</Td>
                    <Td className="text-right font-mono text-teal-600 font-semibold">{formatINR(p.summary.payable_pending)}</Td>
                    <Td><AgeBadge days={p.aging?.oldest_payable_days} testid={`payee-age-bill-${p.id}`} /></Td>
                    <Td><AgeBadge days={p.aging?.oldest_advance_days} testid={`payee-age-adv-${p.id}`} /></Td>
                    <Td className="text-slate-400 text-xs">{p.summary.last_txn_date ? p.summary.last_txn_date : "-"}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </div>
        </>
      )}
    </div>
  );
}
